import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../common/BaseComponent';
import { BlockTextComponent } from '../../../common/block-text/block-text.component';
import { VideoUploadComponent } from '../../../common/video-upload/video-upload.component';
import { NotificationComponent } from '../../../common/notification/notification.component';
import { PinComponent } from '../../../common/pin/pin.component';
import { Helper } from '../../../../../utils/helper';
import { ToastBarService } from '../../../../../services/toast-bar.service';
import { GlobalTemplateService } from '../../../../../services/global-template.service';
import {
  MetaCarouselCardFormat,
  MetaCarouselCardInitModel,
  MetaCarouselCardInitVariables,
  MetaCarouselCardModel,
  MetaCarouselCardVariables,
} from '../../../../../models/meta-template/meta-carousel-card.interface';

/**
 * One card of a Meta carousel ad. Cards are repeatable blocks (2–10, enforced by the template's block counts);
 * media checks happen in the upload controls, presence of media is enforced on save via requiredFields.
 */
@Component({
  selector: 'app-meta-carousel-card',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BlockTextComponent,
    VideoUploadComponent,
    NotificationComponent,
    PinComponent,
  ],
  templateUrl: './meta-carousel-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './meta-carousel-card.component.scss',
})
export class MetaCarouselCardComponent extends BaseComponent implements OnInit, OnDestroy {
  //@ input
  @Input() block: MetaCarouselCardModel = new MetaCarouselCardInitModel();
  @Input() isPreview = false;
  //@ output
  @Output() blockData = new EventEmitter<any>(); // Event emitter to send data to the parent
  @Output() removeBlockEvent = new EventEmitter<string | null>(); // New EventEmitter for remove

  //@ placeholders
  headlinePlaceholder: string = 'Enter headline (max 25 characters)';
  linkPlaceholder: string = 'Enter landing page URL';

  private isTemplateNew = false;
  private _destroy$ = new Subject<void>();

  constructor(
    toastBarService: ToastBarService,
    private globalTemplateService: GlobalTemplateService
  ) {
    super(toastBarService);
  }

  ngOnInit(): void {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new MetaCarouselCardInitVariables();
    }
    // A card always has a format, so the matching upload control renders.
    const variables = this.block.variables as { [key: string]: string };
    if (!variables['Format']) {
      variables['Format'] = 'Image';
    }

    this.globalTemplateService
      .isNewTemplateData$
      .pipe(takeUntil(this._destroy$))
      .subscribe((res: boolean) => {
        this.isTemplateNew = res;
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  get format(): MetaCarouselCardFormat {
    const variables = this.block.variables as { [key: string]: string } | undefined;
    return variables?.['Format'] === 'Video' ? 'Video' : 'Image';
  }

  /**
   * Switching kind drops the current creative; on a saved template its blob is queued for deletion
   * and removed when the template is saved; on a new template it is only a local blob URL.
   */
  setFormat(format: MetaCarouselCardFormat): void {
    if (format === this.format) return;
    const variables = this.block.variables as { [key: string]: string } | undefined;
    if (!variables) return;

    const media = variables['Media'];
    if (media && !this.isTemplateNew) {
      this.globalTemplateService.setMediaForDeletionData(media);
    }

    variables['Format'] = format;
    variables['Media'] = '';
    this.blockData.emit(this.block);
  }

  //@remove
  removeBlock(): void {
    this.removeBlockEvent.emit(this.block.blockId); // Emit the ID to parent
  }

  //@onChange
  onChange(value: any, fieldName: keyof MetaCarouselCardVariables) {
    if (this.block.variables) {
      this.block.variables[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }

  onContainerClick(event: MouseEvent) {
    // Forward the click event to the child app-pin component
    this.pinComponent.onContainerClick(event);
  }
}
